import { PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef } from "react";
import { StyledRoot, StyledResizeHandle } from "./styled";
import { IRootProps } from "./types";
import { useDrawerStore } from "src/store/drawerStore";
import { useSidebarStore } from "src/store/sidebarStore";
import { SIDEBAR_WIDTH_COLLAPSED, SIDEBAR_WIDTH_EXPANDED } from "src/lib/consts/sidebarWidth";
import { drawerWidth } from "src/lib/consts/drawerWidth";

// Never lets the panel shrink small enough to break the message/composer layout.
const MIN_CHAT_WIDTH = 420;

const Root = ({ children, ...props }: IRootProps) => {
    const open = useDrawerStore((s: any) => s.open);
    const isFullscreen = useDrawerStore((s: any) => s.isFullscreen);
    const chatWidth = useDrawerStore((s: any) => s.width);
    const setWidth = useDrawerStore((s: any) => s.setWidth);
    const setResizing = useDrawerStore((s: any) => s.setResizing);
    const draggingRef = useRef(false);

    const handlePointerMove = useCallback((e: PointerEvent) => {
        if (!draggingRef.current) return;
        // Read the sidebar's current state live (not captured at drag-start) so toggling it
        // mid-drag still clamps correctly.
        const sidebarOpen = useSidebarStore.getState().open;
        const sidebarWidth = sidebarOpen ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED;
        // The drawer is anchored right, so its left edge sits at the cursor — the panel can grow
        // left at most until that edge reaches the sidebar, never past it.
        const maxWidth = window.innerWidth - sidebarWidth;
        const nextWidth = Math.min(maxWidth, Math.max(MIN_CHAT_WIDTH, window.innerWidth - e.clientX));
        setWidth(nextWidth);
    }, [setWidth]);

    const stopResizing = useCallback(() => {
        draggingRef.current = false;
        setResizing(false);
        document.body.style.userSelect = "";
        document.removeEventListener("pointermove", handlePointerMove);
        document.removeEventListener("pointerup", stopResizing);
    }, [handlePointerMove, setResizing]);

    const handlePointerDown = (e: ReactPointerEvent) => {
        if (isFullscreen) return;
        // No preventDefault() here — that would also swallow the browser's synthesized dblclick
        // event on this element. Block text selection during the drag this way instead.
        document.body.style.userSelect = "none";
        draggingRef.current = true;
        setResizing(true);
        document.addEventListener("pointermove", handlePointerMove);
        document.addEventListener("pointerup", stopResizing);
    };

    // Safety net if the component unmounts mid-drag (e.g. the chat gets closed while resizing).
    useEffect(() => () => stopResizing(), [stopResizing]);

    const handleDoubleClick = () => setWidth(drawerWidth);

    return (
        <StyledRoot
            variant="persistent"
            anchor="right"
            open={open}
            isFullscreen={isFullscreen}
            chatWidth={chatWidth}
            {...props}
        >
            {!isFullscreen && (
                <StyledResizeHandle onPointerDown={handlePointerDown} onDoubleClick={handleDoubleClick} />
            )}
            {children}
        </StyledRoot>
    )

}

export {Root}
